import { useState } from "react";
import { describeError, isApiError } from "@/api/client";
import {
  Button,
  Dialog,
  DialogClose,
  Form,
  FormActions,
  InlineAlert,
  RadioGroup,
  Select,
  TextArea,
  TextField,
  useToast,
} from "@/components";
import { MATERIAL_KINDS, NOTE_POSITIONS, useCreateMaterial, type MaterialKind, type NotePosition } from "./api";
import styles from "./MaterialDialogs.module.css";

const CAS_PATTERN = /^\d{2,7}-\d{2}-\d$/;
const SHORT_CODE_PATTERN = /^[A-Za-z0-9]{3,6}$/;

interface Candidate {
  id: string;
  name: string;
}

function candidatesOf(error: unknown): Candidate[] {
  if (!isApiError(error) || error.code !== "cas_ambiguous") return [];
  const body = error.body as { candidates?: Candidate[] } | undefined;
  return body?.candidates ?? [];
}

/** A private material in this workspace, linked to a known substance when its CAS number matches one. */
export function CreateMaterialDialog({
  open,
  onOpenChange,
  initialName = "",
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialName?: string;
  onCreated: (id: string) => void;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="New material"
      description="A private material for this workspace. A CAS number links it to what is known about the substance."
    >
      {open ? (
        <CreateMaterialForm
          initialName={initialName}
          onCreated={(id) => {
            onOpenChange(false);
            onCreated(id);
          }}
        />
      ) : null}
    </Dialog>
  );
}

function CreateMaterialForm({ initialName, onCreated }: { initialName: string; onCreated: (id: string) => void }) {
  const create = useCreateMaterial();
  const toast = useToast();
  const [name, setName] = useState(initialName);
  const [kind, setKind] = useState<MaterialKind | null>(null);
  const [cas, setCas] = useState("");
  const [shortCode, setShortCode] = useState("");
  const [note, setNote] = useState<NotePosition | null>(null);
  const [tradeName, setTradeName] = useState("");
  const [summary, setSummary] = useState("");
  const [substanceId, setSubstanceId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const candidates = candidatesOf(create.error);

  function validate(): Record<string, string> {
    const found: Record<string, string> = {};
    if (!name.trim()) found.name = "Give the material a name";
    if (cas.trim() && !CAS_PATTERN.test(cas.trim())) found.cas = "CAS numbers look like 106-22-9";
    if (shortCode.trim() && !SHORT_CODE_PATTERN.test(shortCode.trim())) {
      found.short_code = "Use 3–6 letters or digits, like HED";
    }
    return found;
  }

  function submit() {
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) return;
    create.mutate(
      {
        name: name.trim(),
        kind,
        cas: cas.trim() || null,
        substance_id: substanceId,
        short_code: shortCode.trim() || null,
        note_position: note,
        trade_name: tradeName.trim() || null,
        odor_summary: summary.trim() || null,
      },
      {
        onSuccess: (detail) => {
          toast.success(`${detail.material.name} created`);
          onCreated(detail.material.id);
        },
        onError: (error) => {
          if (!isApiError(error)) return;
          if (error.code === "invalid_cas") setErrors({ cas: error.detail });
          else if (error.code === "short_code_taken" || error.code === "invalid_short_code") {
            setErrors({ short_code: error.detail });
          } else if (Object.keys(error.fieldErrors).length) setErrors({ ...error.fieldErrors });
        },
      },
    );
  }

  const generalError =
    create.error && Object.keys(errors).length === 0 && candidates.length === 0 ? describeError(create.error) : null;

  return (
    <Form onSubmit={submit} errors={errors}>
      <div className={styles.fields}>
        <TextField label="Name" name="name" value={name} onValueChange={setName} required error={errors.name} />
        <div className={styles.row}>
          <Select
            label="Kind"
            name="kind"
            optional
            placeholder="From the substance"
            options={MATERIAL_KINDS}
            value={kind}
            onValueChange={setKind}
          />
          <Select
            label="Note position"
            name="note_position"
            optional
            options={NOTE_POSITIONS}
            value={note}
            onValueChange={setNote}
          />
        </div>
        <div className={styles.row}>
          <TextField
            label="CAS number"
            name="cas"
            optional
            mono
            inputMode="numeric"
            placeholder="106-22-9"
            value={cas}
            onValueChange={(value) => {
              setCas(value);
              setSubstanceId(null);
            }}
            error={errors.cas}
          />
          <TextField
            label="Short code"
            name="short_code"
            optional
            mono
            maxLength={6}
            description="3–6 characters for labels and quick search"
            value={shortCode}
            onValueChange={(value) => setShortCode(value.toUpperCase())}
            error={errors.short_code}
          />
        </div>
        {candidates.length ? (
          <RadioGroup
            label={`Several substances carry CAS ${cas.trim()}. Which one is it?`}
            options={candidates.map((candidate) => ({ value: candidate.id, label: candidate.name }))}
            value={substanceId ?? undefined}
            onValueChange={setSubstanceId}
          />
        ) : null}
        <TextField label="Trade name" name="trade_name" optional value={tradeName} onValueChange={setTradeName} />
        <TextArea
          label="Odor summary"
          name="odor_summary"
          optional
          rows={3}
          value={summary}
          onValueChange={setSummary}
          placeholder="How it smells to you"
        />
        {generalError ? (
          <InlineAlert tone="danger" title="Couldn’t create the material">
            {generalError}
          </InlineAlert>
        ) : null}
      </div>
      <FormActions>
        <DialogClose />
        <Button variant="primary" type="submit" pending={create.isPending} pendingLabel="Creating…">
          Create material
        </Button>
      </FormActions>
    </Form>
  );
}
